import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import notificationsApi from '../api/notifications';

// ── Relative time helper ──────────────────────────────────────────────────────

function relativeTime(isoString) {
  const seconds = Math.floor((Date.now() - new Date(isoString)) / 1000);
  if (seconds < 60)    return 'just now';
  if (seconds < 3600)  return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

// ── Type colour accent ────────────────────────────────────────────────────────

const TYPE_COLORS = {
  SWAP_AGREED:   'bg-yellow-400',
  SWAP_APPROVED: 'bg-green-500',
  SWAP_REJECTED: 'bg-red-500',
};

// ── Bell SVG ──────────────────────────────────────────────────────────────────

function BellIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref  = useRef(null);
  const qc   = useQueryClient();

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn:  notificationsApi.getAll,
    refetchInterval: 30_000,             // poll every 30 s
    refetchIntervalInBackground: false,  // stop polling when tab is hidden
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const refetch = () => qc.invalidateQueries({ queryKey: ['notifications'] });

  const readMutation = useMutation({
    mutationFn: notificationsApi.markRead,
    onSuccess:  refetch,
  });

  const readAllMutation = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess:  refetch,
  });

  return (
    <div ref={ref} className="relative">
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="relative rounded-md p-2 text-gray-500 hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 rounded-xl border border-gray-200 bg-white shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-800">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 rounded-full bg-red-100 text-red-600 px-1.5 py-0.5 text-[10px] font-bold">
                  {unreadCount} new
                </span>
              )}
            </p>
            {unreadCount > 0 && (
              <button
                onClick={() => readAllMutation.mutate()}
                disabled={readAllMutation.isPending}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-40"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
            {notifications.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-gray-400">
                No notifications yet
              </p>
            ) : (
              notifications.slice(0, 20).map(n => (
                <div
                  key={n.id}
                  onClick={() => !n.is_read && readMutation.mutate(n.id)}
                  className={`flex items-start gap-3 px-4 py-3 text-sm transition-colors ${
                    n.is_read
                      ? 'text-gray-500'
                      : 'bg-indigo-50 cursor-pointer hover:bg-indigo-100'
                  }`}
                >
                  {/* Colour dot: type indicator */}
                  <span className={`mt-1.5 flex-shrink-0 h-2 w-2 rounded-full ${
                    n.is_read ? 'bg-gray-300' : (TYPE_COLORS[n.notification_type] ?? 'bg-indigo-500')
                  }`} />

                  <div className="min-w-0">
                    <p className={`leading-snug ${!n.is_read ? 'font-medium text-gray-900' : ''}`}>
                      {n.message}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-400">{relativeTime(n.created_at)}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-gray-100 px-4 py-2 text-center">
              <p className="text-xs text-gray-400">
                Showing {Math.min(notifications.length, 20)} of {notifications.length}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
